import { api } from '../api/client';
import { API_FILES_URL } from '../utils';
import { onSessionChange } from '../utils/session';

/**
 * The files service (keepr-api A5). Files are API-proxied — GET /api/files
 * requires the JWT, so a plain <img src> can't load them; fetch the bytes
 * with the authenticated client and hand back an object URL instead.
 *
 * File ids are IMMUTABLE (replacing a file mints a new id, and the server
 * sends Cache-Control: immutable), so object URLs are cached for the life of
 * the page: one fetch per id+variant, shared by every avatar that renders it.
 */

const objectUrlCache = new Map(); // `${fileId}:${variant}` -> Promise<string>
// Fetched with THIS account's token (the API checks access per file): the
// next account asks again. The object URLs are not revoked here — an image
// still on screen for the frame before it re-renders would break.
onSessionChange(() => objectUrlCache.clear());

export async function uploadFile(file, kind = 'avatar') {
	const form = new FormData();
	form.append('kind', kind);
	form.append('file', file);
	const { data } = await api.post(API_FILES_URL, form);
	return data; // serialized file doc; _id is the avatar fileId
}

export function getFileObjectUrl(fileId, variant = 'sm') {
	const key = `${fileId}:${variant}`;
	if (!objectUrlCache.has(key)) {
		const promise = api
			.get(`${API_FILES_URL}/${fileId}`, { params: { variant }, responseType: 'blob' })
			.then(({ data }) => URL.createObjectURL(data))
			.catch(err => {
				// A failed fetch must not poison the cache — the next render
				// retries (e.g. transient network trouble).
				objectUrlCache.delete(key);
				throw err;
			});
		objectUrlCache.set(key, promise);
	}
	return objectUrlCache.get(key);
}

export async function deleteFile(fileId) {
	await api.delete(`${API_FILES_URL}/${fileId}`);
}
